// @flow

import classNames from 'classnames';
import React from 'react';
import type { Node } from 'react';
import { FormattedMessage, FormattedDate } from 'react-intl';
import { BlurredBanner, Link, Button, StickyScrollParent, MediaQuery } from '@riseart/common';
import { Section, Wrapper } from '@riseart/layout';
import { Article, ArticleHeader, ArticleBody, ArticleFooter } from '@riseart/article';
import { ShareList } from '@riseart/share';
import { Picture } from 'shared_components/common/artdirection/picture/Picture';
import { Share } from 'shared_components/common/share/Share';
import { Breadcrumbs } from 'shared_components/common/breadcrumbs/Breadcrumbs';

import {
  raScreenSm,
  raScreenLgMax,
  raScreenXl,
} from '@riseart/antd-provider/dist/website/variables.less';
import {
  blogArticleShareLabelCls,
  blogArticleWrapperCls,
  blogArticleWrapperWithBannerCls,
} from 'shared_components/pages/blog/Article.less';

type Props = {
  article: Object,
  articleCategory: Object,
  bannerImage: Object,
  metaData: Object,
  canonicalURL: string,
  articleCategoryUrl: string,
  authorUrl: string,
  shareContentKey: string,
  showDate?: boolean,
  listTitle: string,
  listUrl: string,
};

/**
 * ArticleSection
 *
 * @param {Props} props
 * @returns {Node}
 */
export function ArticleSection({
  article,
  articleCategory,
  articleCategoryUrl,
  authorUrl,
  bannerImage,
  metaData,
  canonicalURL,
  shareContentKey,
  showDate = true,
  listTitle,
  listUrl,
}: Props): Node {
  if (!article) {
    return null;
  }

  return (
    <React.Fragment>
      {bannerImage ? (
        <BlurredBanner>
          <Picture
            type="banner.fullWidth"
            artDirectionKey="common.banner"
            image={bannerImage}
            alt={article.title}
          />
        </BlurredBanner>
      ) : null}
      <Section padding="none">
        <Wrapper
          className={classNames(
            blogArticleWrapperCls,
            bannerImage && blogArticleWrapperWithBannerCls,
          )}
          sizes={{
            xs: 'wide',
            sm: 'wide',
            md: 'wide',
            lg: 'narrow',
            xl: 'narrow',
          }}
        >
          <MediaQuery minWidth={raScreenXl}>
            <StickyScrollParent>
              <FormattedMessage id="common.share">
                {(label: string) => <div className={blogArticleShareLabelCls}>{label}</div>}
              </FormattedMessage>
              <Share
                component={ShareList}
                hideLabels
                theme="dark"
                contentKey={shareContentKey}
                contentValues={{ title: article.title }}
                image={metaData.image}
                url={canonicalURL}
              />
            </StickyScrollParent>
          </MediaQuery>
          <Breadcrumbs
            links={[
              {
                isLink: true,
                name: listTitle,
                url: listUrl,
              },
              ...(articleCategory && articleCategory.name
                ? [
                    {
                      isLink: true,
                      name: articleCategory.name,
                      url: articleCategoryUrl,
                    },
                  ]
                : []),
              ...(article
                ? [
                    {
                      isLink: false,
                      name: article.title,
                    },
                  ]
                : []),
            ]}
          />
          <Article>
            <ArticleHeader
              title={article.title}
              category={
                <Link to={articleCategoryUrl} title={articleCategory.name}>
                  {articleCategory.name}
                </Link>
              }
              details={
                <React.Fragment>
                  <FormattedMessage
                    id="common.byName"
                    values={{
                      name: authorUrl ? (
                        <Link to={authorUrl} title={article.author.fullName}>
                          {article.author.fullName}
                        </Link>
                      ) : (
                        article.author.fullName
                      ),
                    }}
                  />{' '}
                  {showDate ? (
                    <React.Fragment>
                      |{' '}
                      <FormattedDate
                        value={new Date(article.publishedDate)}
                        year="numeric"
                        month="short"
                        day="2-digit"
                      >
                        {(dateLabel: string) => dateLabel}
                      </FormattedDate>
                    </React.Fragment>
                  ) : null}
                  <MediaQuery maxWidth={raScreenLgMax}>
                    <Share
                      theme="dark"
                      contentKey="article"
                      contentValues={{ title: article.title }}
                      image={metaData.image}
                      url={canonicalURL}
                      buttonText={
                        <MediaQuery minWidth={raScreenSm}>
                          <FormattedMessage id="common.share" />
                        </MediaQuery>
                      }
                    />
                  </MediaQuery>
                </React.Fragment>
              }
            >
              {article.summary}
            </ArticleHeader>
            <ArticleBody>{article.text}</ArticleBody>
            {article.actionLink && article.actionText ? (
              <ArticleFooter>
                <Button isAnchor external href={article.actionLink} title={article.actionText}>
                  {article.actionText}
                </Button>
              </ArticleFooter>
            ) : null}
          </Article>
        </Wrapper>
      </Section>
    </React.Fragment>
  );
}
